import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/toaster";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Analytics from "./components/Analytics";
import SocialProofPopup from "./components/SocialProofPopup";
import MobileStickyBar from "./components/MobileStickyBar";
import useCanonical from "./hooks/useCanonical";
import ChatWidget from "./components/ChatWidget";
import ExitIntentPopup from "./components/ExitIntentPopup";
import Home from "./pages/Home";
import LoanRequest from "./pages/LoanRequest";
import CreditAnalysis from "./pages/CreditAnalysis";
import OurTeam from "./pages/OurTeam";
import Contact from "./pages/Contact";
import AdminDashboard from "./pages/AdminDashboard";
import ThankYou from "./pages/ThankYou";
import NotFound from "./pages/NotFound";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import LoanRequest2eChance from "./pages/LoanRequest2eChance";
import LoanRequest3eChance from "./pages/LoanRequest3eChance";
import CityLandingPage from "./pages/CityLandingPage";
import FAQPage from "./pages/FAQPage";
import EligibilityTool from "./pages/EligibilityTool";
import LoanCalculator from "./pages/LoanCalculator";
import RebuildCredit from "./pages/RebuildCredit";
import AboutUs from "./pages/AboutUs";
import CreditScore from "./pages/CreditScore";
import VehicleShowcase from "./pages/VehicleShowcase";
import VehicleDetail from "./pages/VehicleDetail";
import { InventoryGate } from "./components/InventoryGate";

function ScrollToTop() {
  const { pathname } = useLocation();
  useCanonical();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <div className="App">
          <ScrollToTop />
          <Analytics />
          <Navbar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/demander-un-pret-auto" element={<LoanRequest />} />
            <Route path="/loan-request" element={<LoanRequest />} />
            <Route path="/credit-analysis" element={<CreditAnalysis />} />
            <Route path="/notre-equipe" element={<OurTeam />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/merci" element={<ThankYou />} />
            <Route path="/pret-auto-2e-chance" element={<LoanRequest2eChance />} />
            <Route path="/pret-auto-3e-chance" element={<LoanRequest3eChance />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/verifier-admissibilite" element={<EligibilityTool />} />
            <Route path="/calculateur-pret-auto" element={<LoanCalculator />} />
            <Route path="/retablir-son-credit" element={<RebuildCredit />} />
            <Route path="/qui-sommes-nous" element={<AboutUs />} />
            <Route path="/cote-de-credit-pret-auto" element={<CreditScore />} />
            <Route path="/nos-vehicules" element={<InventoryGate><VehicleShowcase /></InventoryGate>} />
            <Route path="/nos-vehicules/:id" element={<InventoryGate><VehicleDetail /></InventoryGate>} />
            {/* City-specific SEO landing pages */}
            <Route path="/financement-auto-laval" element={<CityLandingPage city="Laval" cityFr="Laval" region="la Rive-Nord" seoKeywordFr="Financement Auto Laval" />} />
            <Route path="/financement-auto-longueuil" element={<CityLandingPage city="Longueuil" cityFr="Longueuil" region="la Rive-Sud" seoKeywordFr="Financement Auto Longueuil" />} />
            <Route path="/financement-auto-rive-sud" element={<CityLandingPage city="South Shore" cityFr="Rive-Sud" region="la Rive-Sud" seoKeywordFr="Financement Auto Rive-Sud" />} />
            <Route path="/financement-auto-montreal" element={<CityLandingPage city="Montreal" cityFr="Montreal" region="le Grand Montreal" seoKeywordFr="Financement Auto Montreal" />} />
            <Route path="/financement-auto-sherbrooke" element={<CityLandingPage city="Sherbrooke" cityFr="Sherbrooke" region="l'Estrie" seoKeywordFr="Financement Auto Sherbrooke" />} />
            <Route path="/financement-auto-gatineau" element={<CityLandingPage city="Gatineau" cityFr="Gatineau" region="l'Outaouais" seoKeywordFr="Financement Auto Gatineau" />} />
            <Route path="/financement-auto-trois-rivieres" element={<CityLandingPage city="Trois-Rivieres" cityFr="Trois-Rivieres" region="la Mauricie" seoKeywordFr="Financement Auto Trois-Rivieres" />} />
            <Route path="/financement-auto-quebec" element={<CityLandingPage city="Quebec City" cityFr="Quebec" region="la Capitale-Nationale" seoKeywordFr="Financement Auto Quebec" />} />
            <Route path="/financement-auto-drummondville" element={<CityLandingPage city="Drummondville" cityFr="Drummondville" region="le Centre-du-Quebec" seoKeywordFr="Financement Auto Drummondville" />} />
            <Route path="/financement-auto-saint-jean" element={<CityLandingPage city="Saint-Jean-sur-Richelieu" cityFr="Saint-Jean-sur-Richelieu" region="la Monteregie" seoKeywordFr="Financement Auto Saint-Jean-sur-Richelieu" />} />
            <Route path="/financement-auto-terrebonne" element={<CityLandingPage city="Terrebonne" cityFr="Terrebonne" region="la Rive-Nord" seoKeywordFr="Financement Auto Terrebonne" />} />
            <Route path="/financement-auto-repentigny" element={<CityLandingPage city="Repentigny" cityFr="Repentigny" region="Lanaudiere" seoKeywordFr="Financement Auto Repentigny" />} />
            <Route path="/financement-auto-brossard" element={<CityLandingPage city="Brossard" cityFr="Brossard" region="la Rive-Sud" seoKeywordFr="Financement Auto Brossard" />} />
            {/* Catch-all 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
          <SocialProofPopup />
          <MobileStickyBar />
          <ChatWidget />
          <ExitIntentPopup />
          <Toaster />
        </div>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
